import React from 'react';
import { Building } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { formatCurrency } from '@/utils/currency';
import { formatCompactNumber } from '@/lib/utils/formatNumber';

interface DashboardStatsProps {
  stats: any;
  statsLoading: boolean;
  brandsCount: number | null;
  brandsLoading: boolean;
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({ stats, statsLoading, brandsCount, brandsLoading }) => {
  const { userCountry } = useAuth();
  const { currentOrganization } = useOrganization();
  const isBrandOrg = currentOrganization?.organization_type === 'brand';

  const gridCols = isBrandOrg ? 'lg:grid-cols-4' : 'lg:grid-cols-5';
  const skeletonCount = isBrandOrg ? 4 : 5;

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-6`}>
      {statsLoading || brandsLoading
        ? Array(skeletonCount).fill(null).map((_, idx) => (
          <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse h-32" />
        ))
        : stats && (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{stats.activeCampaigns}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Influencers</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{stats.totalInfluencers}</p>
                </div>
              </div>
            </div>
            {!isBrandOrg && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Brands</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{brandsCount ?? 0}</p>
                  </div>
                  <Building className="w-8 h-8 text-purple-600" />
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Reach</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{formatCompactNumber(stats.totalReach)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {formatCurrency(stats.revenue, userCountry)}
                  </p>
                </div>
              </div>
            </div>
          </>
        )
      }
    </div>
  );
};
